/* eslint-disable import/no-cycle */
import { BondTypeValue } from 'routes/Profile/components/ProfileContent/BondAndInsurance/types';
import { NotaryDocumentType } from 'routes/Profile/components/ProfileContent/types';

import { IAppointment } from './appointments';
import { ICommonPayload } from './common';
import { ICompany } from './company';
import { IInvoice } from './invoice';
import { ILocation, IUsersLocations } from './location';
import { IPriceSettings } from './services';
import { IAddress, ITransaction } from './transaction';
import { IUserReview } from './userReview';
import { IWorkDay } from './workday';

export enum RoleEnum {
  ADMIN = 'ADMIN',
  NOTARY = 'NOTARY',
  CLIENT = 'CLIENT',
  SIGNATORY = 'SIGNATORY',
}

export enum CompanyRoleEnum {
  ADMIN = 'ADMIN',
  REGULAR = 'REGULAR',
}

export enum UserStatusEnum {
  SENT = 'SENT',
  REGISTERED = 'REGISTERED',
  ACTIVE = 'ACTIVE',
  DISABLED = 'DISABLED',
}

export enum TokenType {
  REGISTER = 'signup',
  RESET_PASSWORD = 'reset-password',
}

export interface UserEmail {
  email: string;
}

export interface UserPayload extends ICommonPayload, UserEmail {
  password: string;
  rememberMe?: boolean;
}

export interface IInviteUserPayload extends Record<string, any> {
  email: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  role?: RoleEnum | CompanyRoleEnum;
  company?: string;
  serviceTypes?: string[];
  isAdmin?: boolean;
}

export interface UserSignupPayload {
  firstName: string;
  lastName: string;
  password: string;
}

export interface ResetPasswordPayload {
  password: string;
}

export interface TokenPayload {
  token: string;
  userId?: string;
  type: TokenType;
}

export interface LogoutPayload {
  redirectUrl?: string;
}

export interface IOtherEmailType {
  type: string;
  address: string;
  id?: string;
  priority?: number;
}

export enum ContactNumberType {
  CELL = 'CELL',
  PHONE = 'PHONE',
  FAX = 'FAX',
}

export interface IContactType {
  type: string;
  numberType: ContactNumberType;
  number: string;
  id?: string;
  priority?: number;
}

export interface IFileType {
  id: string;
  name: string;
  type: string;
  extension: string;
  path: string;
  pipeFrom: string;
  thumbnailPipeFrom: string;
  document?: string;
  user?: string;
}

export interface ICommissionDocumentDetails {
  commissionPeriod: string[];
  commissionNo: string;
  state: string;
  county: string;
}

export interface IBondDocumentDetails {
  company: string;
  policyEffectiveDate: string[];
  policyNo: string;
  bondType: BondTypeValue;
}

export interface IDocumentType<T = any> {
  id: string;
  type: NotaryDocumentType;
  files: IFileType[];
  details: T;
}

export type IUserAddress = Partial<IAddress>;

export interface IPaymentAddress extends IUserAddress {
  mailedCheck?: string;
  payableName?: string;
}
// Status : Active/Closed
// Reference #
// Text
// Description
// Text
// Created Date
// Date formatted like (Month DD, YYYY)
// Scheduled date for notarization
// Notary Name
// Summary of signatories (when transaction is first created there will be no signatories, they will be added after the transaction is created)
// Signatory Type
// Signatory Name

export type EmailNotificationType =
  | 'appointment'
  | 'assignment'
  | 'invoice'
  | 'payment'
  | 'profile'
  | 'transaction'
  | 'all';

export type EmailDisableType = {
  [K in EmailNotificationType]: boolean;
};

export interface IUser extends IUserAddress {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  otherEmails: IOtherEmailType[];
  contacts: IContactType[];
  role: RoleEnum;
  availability: IWorkDay[];
  company?: Partial<ICompany>;
  documents?: IDocumentType[];
  paymentDetail?: string;
  paymentAddress?: IPaymentAddress;
  docShippingAddress?: IUserAddress;
  notaryCommissionLicense?: File;
  backgroundCheckReport?: File;
  certifications?: File;
  taxInformation?: File;
  insurancePolicies?: File;
  status?: UserStatusEnum;
  avatar?: IFileType;
  reviewsReceived?: IUserReview[];
  reviewsWritten?: IUserReview[];
  availabilityTimezone?: string;
  isVerified?: boolean;
  transactions: ITransaction[];
  createdTransactions: ITransaction[];
  notaryTransactions: ITransaction[];
  appointments?: IAppointment[];
  invoices: IInvoice[];
  specificPrices?: IPriceSettings[];
  companyRole?: CompanyRoleEnum;
  usersLocations?: IUsersLocations[];
  /* Used in accounting page */
  total: number;
  paid: number;
  balance: number;
  appointmentCount: number;
  emailDisable?: EmailDisableType;
  /* Used for getting invite link */
  inviteUrl?: string;
  isAssignedTransaction?: boolean;
  mainLocation?: ILocation;
  ableScanDoc?: boolean;
  /* Used for getting notary */
  isAvailable?: boolean;
}

export interface INotary extends IUser {
  transactionDistance: number;
}
